import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import Axios, { AxiosResponse } from "axios";
import { DesignerChangeKind, IDesignerPresenceReport } from "./designer-live-types";
import { DesignerSyncService } from "./designer-sync-service";

const backendUrl: string = CommonHelperFunctions.GetBackendUrl();
const REPORT_THROTTLE_MS: number = 500;

/**
 * Project-presence reporter (Faz 5): tells the session which tool this tab is in and which
 * entity it has open. One merged, trailing-throttled POST — tool changes come from the
 * single setDashboardState mutator, entity changes from the per-tool selection hooks
 * (which double as the stale-write "opened" markers).
 */
class DesignerPresenceReporterImpl {
  private clientId: string | null = null;
  private current: IDesignerPresenceReport = { dashboardState: 1 };
  private timer: ReturnType<typeof setTimeout> | null = null;

  public start = (clientId: string): void => {
    this.clientId = clientId;
  };

  /** The entity this tab currently has open — feeds the "same record" marker in the avatar stack. */
  public getCurrentEntityId = (): string | undefined => {
    return this.current.entityId;
  };

  public getClientId = (): string | null => {
    return this.clientId;
  };

  public stop = (): void => {
    this.clientId = null;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  };

  public reportTool = (dashboardState: number): void => {
    // Switching tools always closes the previous tool's editors (toolbar handleRedirect
    // cancels them) — clear the entity and let deferred reloads apply.
    this.current = { dashboardState };
    this.schedule();
  };

  /**
   * Entity opened/closed in the current tool. When staleKind is given, the open moment is
   * also recorded for the stale-write guard (canvas screens skip it — they have op-collab).
   */
  public reportEntity = (
    entityType: string,
    entityId: string | undefined,
    entityName: string | undefined,
    staleKind?: DesignerChangeKind
  ): void => {
    if (staleKind) DesignerSyncService.markEntityOpened(staleKind, entityId);
    this.current = {
      dashboardState: this.current.dashboardState,
      entityType: entityId ? entityType : undefined,
      entityId,
      entityName: entityId ? entityName : undefined
    };
    this.schedule();
  };

  private schedule = (): void => {
    if (!this.clientId || this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.send();
    }, REPORT_THROTTLE_MS);
  };

  private send = async (): Promise<void> => {
    if (!this.clientId) return;
    try {
      await Axios.post<null, AxiosResponse>(`${backendUrl}/designer/presence`, {
        clientId: this.clientId,
        ...this.current
      });
    } catch {
      // Presence is best-effort — never surface an error for it.
    }
  };
}

export const DesignerPresenceReporter: DesignerPresenceReporterImpl = new DesignerPresenceReporterImpl();
